export default class LibsynPlayer {
    constructor() {
        this.players = [];
        this.init();
    }

    init() {
        // Initialize all custom audio players on the page
        const customPlayers = document.querySelectorAll('.custom-audio-player');
        if (customPlayers.length > 0) {
            customPlayers.forEach(playerContainer => {
                this.setupPlayer(playerContainer);
            });
        }
    }

    setupPlayer(playerContainer) {
        const audioElement = playerContainer.querySelector('audio');
        if (!audioElement) return;

        const playerId = audioElement.id.split('-')[1];
        const playPauseBtn = playerContainer.querySelector('.play-pause-btn');
        const timeline = playerContainer.querySelector('.timeline');
        const progressBar = playerContainer.querySelector('.progress');
        const currentTimeEl = playerContainer.querySelector('.current-time');
        const durationEl = playerContainer.querySelector('.duration');
        const muteBtn = playerContainer.querySelector('.mute-btn');
        const volumeSlider = playerContainer.querySelector('.volume-slider');
        const volumeProgress = playerContainer.querySelector('.volume-progress-bar');
        const speedButtons = playerContainer.querySelectorAll('.speed-controls button');
        const menuBtn = playerContainer.querySelector('.menu-btn');
        const menuDropdown = playerContainer.querySelector('.menu-dropdown');

        // Store player instance
        const player = {
            container: playerContainer,
            audio: audioElement,
            playerId: playerId,
            isPlaying: false,
            isMuted: false
        };
        this.players.push(player);

        // Set up event listeners
        this.setupPlayPause(player, playPauseBtn);
        this.setupTimelineControls(player, timeline, progressBar, currentTimeEl, durationEl);
        this.setupVolumeControls(player, muteBtn, volumeSlider, volumeProgress);
        this.setupSpeedControls(player, speedButtons);
        this.setupMenuControls(player, menuBtn, menuDropdown);

        // Initial setup
        this.updateDurationDisplay(player, durationEl);
        
        // Set initial volume
        audioElement.volume = 0.8;
        volumeProgress.style.width = '80%';
    }

    setupPlayPause(player, playPauseBtn) {
        if (!playPauseBtn) return;

        playPauseBtn.addEventListener('click', () => {
            if (player.isPlaying) {
                player.audio.pause();
            } else {
                // Pause all other players first
                this.players.forEach(p => {
                    if (p.playerId !== player.playerId && p.isPlaying) {
                        p.audio.pause();
                        p.isPlaying = false;
                        p.container.querySelector('.play-pause-btn').classList.remove('playing');
                    }
                });
                player.audio.play();
            }
        });

        player.audio.addEventListener('play', () => {
            player.isPlaying = true;
            playPauseBtn.classList.add('playing');
        });

        player.audio.addEventListener('pause', () => {
            player.isPlaying = false;
            playPauseBtn.classList.remove('playing');
        });

        player.audio.addEventListener('ended', () => {
            player.isPlaying = false;
            playPauseBtn.classList.remove('playing');
        });
    }

    setupTimelineControls(player, timeline, progressBar, currentTimeEl, durationEl) {
        if (!timeline || !progressBar) return;

        // Update progress bar as audio plays
        player.audio.addEventListener('timeupdate', () => {
            const currentTime = player.audio.currentTime;
            const duration = player.audio.duration || 0;
            
            if (duration > 0) {
                const progressPercent = (currentTime / duration) * 100;
                progressBar.style.width = `${progressPercent}%`;
                this.updateTimeDisplay(currentTimeEl, currentTime);
            }
        });

        // Allow seeking by clicking on timeline
        timeline.addEventListener('click', (e) => {
            const timelineWidth = timeline.clientWidth;
            const clickPosition = e.offsetX;
            const duration = player.audio.duration;
            
            player.audio.currentTime = (clickPosition / timelineWidth) * duration;
        });

        // Update duration display when metadata is loaded
        player.audio.addEventListener('loadedmetadata', () => {
            this.updateDurationDisplay(player, durationEl);
        });
    }

    setupVolumeControls(player, muteBtn, volumeSlider, volumeProgress) {
        if (!muteBtn || !volumeSlider) return;

        // Toggle mute and show volume slider
        muteBtn.addEventListener('click', () => {
            // Toggle mute state
            player.isMuted = !player.isMuted;
            player.audio.muted = player.isMuted;
            muteBtn.classList.toggle('muted', player.isMuted);
            
            if (player.isMuted) {
                volumeProgress.style.width = '0%';
            } else {
                volumeProgress.style.width = `${player.audio.volume * 100}%`;
            }
            
            // Toggle volume slider visibility
            volumeSlider.classList.toggle('active');
            
            // Close all other volume sliders
            this.players.forEach(p => {
                if (p.playerId !== player.playerId) {
                    const otherVolumeSlider = p.container.querySelector('.volume-slider');
                    if (otherVolumeSlider) {
                        otherVolumeSlider.classList.remove('active');
                    }
                }
            });
        });

        // Adjust volume by clicking on volume slider
        volumeSlider.addEventListener('click', (e) => {
            const sliderWidth = volumeSlider.clientWidth;
            const clickPosition = e.offsetX;
            const newVolume = clickPosition / sliderWidth;
            
            player.audio.volume = newVolume;
            volumeProgress.style.width = `${newVolume * 100}%`;
            
            // Unmute if was muted
            if (player.isMuted) {
                player.isMuted = false;
                player.audio.muted = false;
                muteBtn.classList.remove('muted');
            }
            
            // Prevent the click from closing the volume slider
            e.stopPropagation();
        });
        
        // Close volume slider when clicking elsewhere
        document.addEventListener('click', (e) => {
            if (!volumeSlider.contains(e.target) && !muteBtn.contains(e.target)) {
                volumeSlider.classList.remove('active');
            }
        });
    }

    setupSpeedControls(player, speedButtons) {
        if (!speedButtons.length) return;

        speedButtons.forEach(button => {
            button.addEventListener('click', () => {
                const speed = parseFloat(button.dataset.speed);
                player.audio.playbackRate = speed;
                
                // Update active button
                speedButtons.forEach(btn => btn.classList.remove('active'));
                button.classList.add('active');
            });
        });
    }

    updateTimeDisplay(element, time) {
        if (!element) return;
        
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        element.textContent = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }

    setupMenuControls(player, menuBtn, menuDropdown) {
        if (!menuBtn || !menuDropdown) return;
        
        // Toggle menu dropdown when clicking the menu button
        menuBtn.addEventListener('click', (e) => {
            // Close all other menus first
            this.players.forEach(p => {
                if (p.playerId !== player.playerId) {
                    const otherMenuDropdown = p.container.querySelector('.menu-dropdown');
                    if (otherMenuDropdown) {
                        otherMenuDropdown.classList.remove('active');
                    }
                }
            });
            
            // Toggle this menu
            menuDropdown.classList.toggle('active');
            e.stopPropagation();
        });
        
        // Close menu when clicking elsewhere
        document.addEventListener('click', (e) => {
            if (!menuDropdown.contains(e.target) && !menuBtn.contains(e.target)) {
                menuDropdown.classList.remove('active');
            }
        });
        
        // Prevent clicks inside the dropdown from closing it
        menuDropdown.addEventListener('click', (e) => {
            e.stopPropagation();
        });
    }
    
    updateDurationDisplay(player, durationEl) {
        if (!durationEl) return;
        
        const duration = player.audio.duration || 0;
        this.updateTimeDisplay(durationEl, duration);
    }

    // Legacy support for the old setSpeed function
    static setSpeed(playerId, speed, button) {
        const player = document.getElementById("podcastPlayer-" + playerId);
        if (player) {
            player.playbackRate = speed;
        }

        const controls = document.getElementById("controls-" + playerId);
        if (controls) {
            const buttons = controls.getElementsByTagName("button");
            for (let i = 0; i < buttons.length; i++) {
                buttons[i].classList.remove("active");
            }
            button.classList.add("active");
        }
    }
}

// Make setSpeed available globally for backward compatibility
window.setSpeed = LibsynPlayer.setSpeed;